import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Mail, Phone, Globe } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import Reveal from "@/components/Reveal";
import { sendContact } from "@/lib/api";
import useHeaderScroll from "@/lib/useHeaderScroll";
import LegalLinks from "@/components/LegalLinks";

function ChipGroup({ title, items, testid }) {
  return (
    <div className="mb-7">
      <h3 className="font-mono-data text-[0.6rem] tracking-[0.24em] text-eegold mb-3">{title.toUpperCase()}</h3>
      <div className="flex flex-wrap gap-1.5" data-testid={testid}>
        {items.map((item) => (
          <span key={item} className="border border-white/25 rounded-full px-2.5 py-1 text-[0.66rem] text-white/80 hover:bg-white/10 hover:border-white/50 transition-colors cursor-default">
            {item}
          </span>
        ))}
      </div>
    </div>
  );
}

export default function ITManagement() {
  const { t } = useLang();
  const it = t.it;
  const [form, setForm] = useState({ name: "", email: "", company: "", message: "" });
  const [status, setStatus] = useState("idle");
  const { scrolled, hidden } = useHeaderScroll();

  const submit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    try {
      await sendContact("it", form.name, form.email, { Company: form.company, Message: form.message });
      setStatus("sent");
      setForm({ name: "", email: "", company: "", message: "" });
    } catch (err) {
      setStatus("error");
    }
  };

  const inputCls = "w-full bg-white border border-[#0a0a0a]/20 text-[#0a0a0a] placeholder:text-[#0a0a0a]/40 focus:border-eeteal px-3.5 py-3 font-itbody text-sm transition-colors";
  const navLinkCls = "hidden md:inline font-mono-data text-[0.62rem] tracking-[0.18em] text-[#0a0a0a]/55 hover:text-eeteal transition-colors";

  return (
    <main className="bg-[#F3F4F6] text-[#0a0a0a] font-itbody" data-testid="it-page">
      {/* Desktop: links + language button pinned to the top-right corner, same as the Real Estate landing */}
      <nav className="hidden md:flex fixed top-4 right-4 z-50 items-center gap-6">
        <a href="#it-process" data-testid="it-nav-process" className={navLinkCls}>{it.navProcess.toUpperCase()}</a>
        <a href="#it-expertise" data-testid="it-nav-expertise" className={navLinkCls}>{it.navExpertise.toUpperCase()}</a>
        <a href="#it-engagements" data-testid="it-nav-engagements" className={navLinkCls}>{it.navEngagements.toUpperCase()}</a>
        <LanguageSwitcher variant="light" />
      </nav>
      <div className="lg:grid lg:grid-cols-[340px_1fr]">
        <aside data-lenis-prevent className="hidden lg:block bg-eeteal text-white lg:sticky lg:top-0 lg:h-screen lg:overflow-y-auto overscroll-contain" data-testid="it-sidebar">
          <div className="p-7">
            <div className="bg-white rounded-sm p-3.5 mb-8">
              <img src="/assets/it-management-logo.webp" alt="Eagle Eyes IT Management" className="w-full h-auto" />
            </div>
            <ChipGroup title={it.servicesTitle} items={it.services} testid="it-services-chips" />
            <ChipGroup title={it.productsTitle} items={it.products} testid="it-products-chips" />
            <ChipGroup title={it.portfolioTitle} items={it.portfolio} testid="it-portfolio-chips" />
          </div>
        </aside>

        <div className="min-w-0">
          <header
            className={`sticky top-0 z-40 flex items-center justify-between gap-6 pl-6 pr-4 md:px-12 py-4 md:py-5 transition-[transform,background-color,border-color,backdrop-filter] duration-300 ease-out ${
              hidden ? "-translate-y-full" : "translate-y-0"
            } ${scrolled ? "bg-[#F3F4F6]/85 backdrop-blur-xl border-b border-[#0a0a0a]/10" : "bg-transparent border-b border-transparent"}`}
            data-testid="it-header"
          >
            <span className="lg:hidden bg-white rounded-sm px-2.5 py-1.5 shadow-sm">
              <img src="/assets/it-management-logo.webp" alt="Eagle Eyes IT Management" className="h-8 w-auto" data-testid="it-header-logo" />
            </span>
            <div className="absolute right-4 top-4 md:hidden"><LanguageSwitcher variant="light" /></div>
          </header>

          <section className="px-6 md:px-12 pt-6 md:pt-12 pb-16 md:pb-20 border-b-2 border-[#0a0a0a] lg:border-b lg:border-[#0a0a0a]/15" data-testid="it-hero">
            <div className="lg:grid lg:grid-cols-[1fr_330px] lg:gap-14">
              <Reveal>
                <p className="font-mono-data text-[0.66rem] tracking-[0.3em] text-eeteal-bright mb-6">{it.kicker}</p>
                <h1 className="font-itd font-semibold leading-[0.95] tracking-tight text-[2.6rem] md:text-[4.6rem]">
                  {it.h1a}
                  <span className="block text-eeteal">{it.h1b}</span>
                </h1>
                <p className="mt-8 max-w-[52ch] text-[1.05rem] leading-[1.65] text-[#0a0a0a]/75">{it.hero}</p>
                <a href="#it-contact" data-testid="it-hero-cta" className="mt-9 inline-flex items-center gap-2 bg-eeteal text-white font-mono-data text-[0.66rem] tracking-[0.18em] px-6 py-4 hover:bg-eeteal-bright transition-colors">
                  {it.heroCta.toUpperCase()} <ArrowRight className="h-3.5 w-3.5" />
                </a>
              </Reveal>
              <Reveal delay={0.15} className="hidden lg:block">
                <div className="pt-[8.2rem]" data-testid="it-profile-desktop">
                  <div className="border-l-[3px] border-eeteal pl-6">
                    <h2 className="font-mono-data text-[0.62rem] tracking-[0.26em] text-eegold mb-4">{it.profileTitle.toUpperCase()}</h2>
                    <p className="text-[0.92rem] leading-[1.7] text-[#0a0a0a]/75">{it.profile}</p>
                  </div>
                </div>
              </Reveal>
            </div>
          </section>

          {/* Mobile only: blue chip column between hero and profile */}
          <section className="lg:hidden bg-eeteal text-white px-6 py-10" data-testid="it-chips-mobile">
            <ChipGroup title={it.servicesTitle} items={it.services} testid="it-services-chips-mobile" />
            <ChipGroup title={it.productsTitle} items={it.products} testid="it-products-chips-mobile" />
            <ChipGroup title={it.portfolioTitle} items={it.portfolio} testid="it-portfolio-chips-mobile" />
          </section>

          {/* Mobile only: profile after chips */}
          <section className="lg:hidden px-6 py-14 border-b border-[#0a0a0a]/15" data-testid="it-profile-mobile">
            <Reveal>
              <h2 className="font-itd font-medium text-[1.6rem] mb-6">{it.profileTitle}</h2>
              <p className="text-[1rem] leading-[1.7] text-[#0a0a0a]/80">{it.profile}</p>
            </Reveal>
          </section>

          <section id="it-process" className="px-6 md:px-12 py-14 md:py-18 border-b border-[#0a0a0a]/15 scroll-mt-8" data-testid="it-process">
            <Reveal><h2 className="font-itd font-medium text-[1.6rem] md:text-[2.1rem] mb-10">{it.processTitle}</h2></Reveal>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {it.process.map((s) => (
                <Reveal key={s.num} delay={Number(s.num) * 0.07}>
                  <div className="border-t-[3px] border-eeteal pt-5" data-testid={`it-process-${s.num}`}>
                    <span className="font-itd font-medium text-eegold text-[1.9rem] leading-none">{s.num}</span>
                    <h3 className="font-itd font-medium text-[1.15rem] mt-3 mb-2">{s.title}</h3>
                    <p className="text-[0.9rem] leading-[1.65] text-[#0a0a0a]/70">{s.text}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </section>

          <section id="it-expertise" className="px-6 md:px-12 py-14 md:py-18 border-b border-[#0a0a0a]/15 scroll-mt-8" data-testid="it-expertise">
            <Reveal><h2 className="font-itd font-medium text-[1.6rem] md:text-[2.1rem] mb-10">{it.expertiseTitle}</h2></Reveal>
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-px bg-[#0a0a0a]/15 border border-[#0a0a0a]/15">
              {it.expertise.map((x, i) => (
                <div key={x.title} className="bg-[#F3F4F6] hover:bg-white p-6 transition-colors duration-300 group" data-testid={`it-expertise-${i}`}>
                  <h3 className="font-itd font-medium text-[0.98rem] mb-2 group-hover:text-eeteal transition-colors">{x.title}</h3>
                  <p className="font-mono-data text-[0.66rem] leading-[1.7] text-[#0a0a0a]/55">{x.items}</p>
                </div>
              ))}
            </div>
          </section>

          <section id="it-engagements" className="px-6 md:px-12 py-14 md:py-18 border-b border-[#0a0a0a]/15 scroll-mt-8" data-testid="it-engagements">
            <Reveal><h2 className="font-itd font-medium text-[1.6rem] md:text-[2.1rem] mb-10">{it.engagementsTitle}</h2></Reveal>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
              {it.engagements.map((e, i) => (
                <Reveal key={e.client} delay={i * 0.07}>
                  <article data-testid={`it-engagement-${i}`}>
                    <h3 className="font-itd font-medium text-[1.15rem] mb-1.5">{e.client}</h3>
                    <span className="font-mono-data text-[0.6rem] tracking-[0.16em] text-eeteal-bright block mb-3">{e.role.toUpperCase()}</span>
                    <p className="text-[0.92rem] leading-[1.65] text-[#0a0a0a]/70">{e.text}</p>
                  </article>
                </Reveal>
              ))}
            </div>
          </section>

          <section id="it-contact" className="px-6 md:px-12 py-14 md:py-18 scroll-mt-8" data-testid="it-contact">
            <Reveal>
              <h2 className="font-itd font-medium text-[1.6rem] md:text-[2.1rem] mb-3">{it.contactTitle}</h2>
              <p className="max-w-[52ch] text-[0.98rem] leading-[1.65] text-[#0a0a0a]/70 mb-8">{it.contactText}</p>
              {status === "sent" ? (
                <p data-testid="it-form-success" className="max-w-xl font-itbody text-sm text-eeteal border border-eeteal/40 bg-white px-5 py-6">{t.form.sent}</p>
              ) : (
                <form className="grid gap-3 max-w-xl" onSubmit={submit} data-testid="it-contact-form">
                  <div className="grid sm:grid-cols-2 gap-3">
                    <input data-testid="it-form-name" type="text" required placeholder={t.form.name} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} />
                    <input data-testid="it-form-email" type="email" required placeholder={t.form.email} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} />
                  </div>
                  <input data-testid="it-form-company" type="text" placeholder={it.companyField} value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className={inputCls} />
                  <textarea data-testid="it-form-message" required rows={5} placeholder={it.messageField} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className={inputCls} />
                  <button data-testid="it-form-submit" type="submit" disabled={status === "sending"} className="justify-self-start mt-1 bg-eeteal text-white font-mono-data text-[0.66rem] tracking-[0.18em] px-6 py-4 hover:bg-eeteal-bright transition-colors disabled:opacity-50">
                    {status === "sending" ? t.form.sending.toUpperCase() : it.contactButton.toUpperCase()}
                  </button>
                  {status === "error" && <p data-testid="it-form-error" className="text-[0.78rem] text-crimson">{t.form.error}</p>}
                </form>
              )}

              <div className="mt-14 max-w-3xl" data-testid="it-contact-block">
                <h3 className="font-mono-data text-[0.62rem] tracking-[0.26em] text-eeteal-bright mb-5">{it.contactBlockTitle.toUpperCase()}</h3>
                <div className="rounded-lg bg-eeteal/10 border border-eeteal/25 p-5 md:p-7">
                  <div className="flex flex-col gap-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <a href="mailto:info.it@eagle-eyes.al" data-testid="it-contact-email" className="flex items-center gap-3 border border-[#0a0a0a]/15 bg-white px-4 py-3.5 hover:border-eeteal transition-colors group">
                      <Mail className="h-4 w-4 text-eeteal shrink-0" />
                      <span className="text-[0.85rem] group-hover:text-eeteal transition-colors">info.it@eagle-eyes.al</span>
                    </a>
                    <span data-testid="it-contact-web" className="flex items-center gap-3 border border-[#0a0a0a]/15 bg-white px-4 py-3.5 group">
                      <Globe className="h-4 w-4 text-eeteal shrink-0" />
                      <span className="text-[0.85rem]">www.eagle-eyes.al/it-management</span>
                    </span>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <a href="tel:+31853697536" data-testid="it-contact-phone-nl" className="flex items-center gap-3 border border-[#0a0a0a]/15 bg-white px-4 py-3.5 hover:border-eeteal transition-colors group">
                      <Phone className="h-4 w-4 text-eeteal shrink-0" />
                      <span className="text-[0.85rem] group-hover:text-eeteal transition-colors">NL +31 85 369 7536</span>
                    </a>
                    <a href="tel:+442037695067" data-testid="it-contact-phone-uk" className="flex items-center gap-3 border border-[#0a0a0a]/15 bg-white px-4 py-3.5 hover:border-eeteal transition-colors group">
                      <Phone className="h-4 w-4 text-eeteal shrink-0" />
                      <span className="text-[0.85rem] group-hover:text-eeteal transition-colors">UK +44 20 3769 5067</span>
                    </a>
                    <a href="tel:+12027739486" data-testid="it-contact-phone-us" className="flex items-center gap-3 border border-[#0a0a0a]/15 bg-white px-4 py-3.5 hover:border-eeteal transition-colors group">
                      <Phone className="h-4 w-4 text-eeteal shrink-0" />
                      <span className="text-[0.85rem] group-hover:text-eeteal transition-colors">US +1 202 773 9486</span>
                    </a>
                    <a href="tel:+355696803306" data-testid="it-contact-phone-al" className="flex items-center gap-3 border border-[#0a0a0a]/15 bg-white px-4 py-3.5 hover:border-eeteal transition-colors group">
                      <Phone className="h-4 w-4 text-eeteal shrink-0" />
                      <span className="text-[0.85rem] group-hover:text-eeteal transition-colors">AL +355 69 680 3306</span>
                    </a>
                  </div>
                </div>
                </div>
              </div>
            </Reveal>
          </section>

          <footer className="px-6 md:px-12 py-10 border-t border-[#0a0a0a]/15 flex flex-wrap items-end justify-between lg:grid lg:grid-cols-[1fr_auto_1fr] gap-6" data-testid="it-footer">
            <div>
              <p className="font-itd font-medium tracking-[0.08em] text-[0.95rem]">{it.footerBrand}</p>
              <p className="font-mono-data text-[0.6rem] tracking-[0.2em] text-[#0a0a0a]/45 mt-2">{t.common.company}</p>
            </div>
            <LegalLinks className="font-mono-data text-[0.6rem] tracking-[0.2em] text-[#0a0a0a]/45 order-last w-full text-center lg:order-none lg:w-auto" />
            <span className="lg:justify-self-end font-mono-data text-[0.6rem] tracking-[0.2em] text-[#0a0a0a]/45">© {new Date().getFullYear()} {it.footerBrand}</span>
          </footer>
        </div>
      </div>
    </main>
  );
}
